// A save that fails must say so and say what to do (DESIGN_SYSTEM §5, never lose work silently).
// The common cause on a phone is a full disk: IndexedDB throws QuotaExceededError. Nothing here
// changes a repo, a transaction or a stored format; it only catches at the call sites.
import { useAppStore } from '../app/store';
import { isQuotaError } from './quota';

export { isQuotaError };

export const FULL_MESSAGE = "Couldn't save. This phone is out of space. Free some in More › Storage, then try again.";
export const FAILED_MESSAGE = "Couldn't save that. Nothing was changed. Try again.";

export function reportWriteFailure(err: unknown): void {
  console.warn('A save failed', err);
  useAppStore.getState().showToast({ message: isQuotaError(err) ? FULL_MESSAGE : FAILED_MESSAGE });
}

/** Runs a write; on failure shows the toast and returns undefined. */
export async function guarded<T>(run: () => Promise<T>): Promise<T | undefined> {
  try {
    return await run();
  } catch (err) {
    reportWriteFailure(err);
    return undefined;
  }
}

/** Wraps the named async store actions with `guarded`, so the state stays as it was and the toast tells why. */
export function guardActions<T extends object>(actions: T, names: (keyof T)[]): T {
  const out = { ...actions };
  for (const name of names) {
    const fn = actions[name];
    if (typeof fn === 'function') out[name] = ((...args: unknown[]) => guarded(async () => (fn as (...a: unknown[]) => unknown)(...args))) as T[keyof T];
  }
  return out;
}

/** The last net: a full disk that nothing else caught still gets the toast instead of a silent failure. */
export function installWriteNet(): void {
  window.addEventListener('unhandledrejection', (e) => {
    if (!isQuotaError(e.reason)) return;
    e.preventDefault();
    reportWriteFailure(e.reason);
  });
}
